import {
  Box,
  Chip,
  Collapse,
  createTheme,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Typography,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { useMemo, useState } from "react";
import { applySortFilter, getComparator } from "src/utils/sharedTables";
import { TablaHead, TablaToolbar } from "../_dashboard/tablas";
import Scrollbar from "../Scrollbar";
import SearchNotFound from "../SearchNotFound";
import { ThemeProvider } from "@material-ui/styles";
import * as locales from "@material-ui/core/locale";
import CustomTablePagination from "../CustomTablePagination";
import {
  capitalizarPalabras,
  capitalizarPrimeraLetra,
} from "src/utils/sharedFunctions";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import { fDateLong } from "src/utils/formatTime";

export default function RowAppTaxpayer({ row }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(INITIAL_STATE_TABLE);

  const handleFilterByName = (event) => {
    setState({ ...state, filterName: event.target.value });
  };

  const filasFiltradas = useMemo(() => {
    const propiedadesParaFiltrar = ["nombre", "refCliente", "sucursal"];
    return applySortFilter(
      row.history,
      getComparator(state.order, state.orderBy),
      state.filterName,
      propiedadesParaFiltrar,
    );
  }, [row.history, state.filterName, state.order, state.orderBy]);

  const handleRequestSort = (event, property) => {
    const isAsc = state.orderBy === property && state.order === "asc";
    setState({ ...state, order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleChangePage = (event, newPage) => {
    setState({ ...state, currentPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setState({
      ...state,
      rowsPerPageOptions: Number(event.target.value),
      currentPage: 0,
    });
  };

  const emptyRows =
    state.currentPage > 0
      ? Math.max(
          0,
          (1 + state.currentPage) * state.rowsPerPageOptions -
            row.history.length,
        )
      : 0;

  const esSucursalNoEncontrada = filasFiltradas.length === 0;

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }} hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {capitalizarPrimeraLetra(row.plan)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, width: "100%" }}>
              <Grid container>
                <Grid
                  item
                  xs={6}
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" gutterBottom component="div">
                    {`Clientes ${row.plan.toLowerCase()}`}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <TablaToolbar
                    filterName={state.filterName}
                    onFilterName={handleFilterByName}
                  />
                </Grid>
              </Grid>
              <Scrollbar>
                <TableContainer>
                  <Table size="small" aria-label="purchases">
                    <TablaHead
                      order={state.order}
                      orderBy={state.orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={row.history?.length}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {filasFiltradas
                        .slice(
                          state.currentPage * state.rowsPerPageOptions,
                          state.currentPage * state.rowsPerPageOptions +
                            state.rowsPerPageOptions,
                        )
                        .map((historyRow, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row">
                              <Typography variant="body2" noWrap>
                                {capitalizarPalabras(historyRow.nombre)}
                              </Typography>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {formatRut(
                                historyRow.refCliente,
                                RutFormat.DOTS_DASH,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {fDateLong(historyRow.fechaCreacion)}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                variant="outlined"
                                label={capitalizarPrimeraLetra(
                                  historyRow.contratoFirmado,
                                )}
                                color={
                                  historyRow.contratoFirmado === "SI"
                                    ? "success"
                                    : "error"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {capitalizarPalabras(historyRow.sucursal)}
                            </TableCell>
                          </TableRow>
                        ))}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                    {esSucursalNoEncontrada && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={state.filterName} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider
                          theme={(outerTheme) =>
                            createTheme(outerTheme, locales[state.locale])
                          }
                        >
                          <CustomTablePagination
                            count={filasFiltradas.length}
                            rowsPerPage={state.rowsPerPageOptions}
                            page={state.currentPage}
                            colSpan={9}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Scrollbar>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const INITIAL_STATE_TABLE = {
  currentPage: 0,
  rowsPerPageOptions: 5,
  order: "asc",
  orderBy: "nombre",
  locale: "esES",
  filterName: "",
};

const TABLE_HEAD = [
  { id: "nombre", label: "Cliente", alignRight: false },
  { id: "refCliente", label: "RUT", alignRight: false },
  { id: "fechaCreacion", label: "Fecha ingreso", alignRight: true },
  { id: "contratoFirmado", label: "Contrato firmado", alignRight: true },
  { id: "sucursal", label: "Sucursal", alignRight: true },
];
